import React, { useState, useEffect, useCallback } from 'react';
import { Formik } from 'formik';
import * as Yup from 'yup';
import {
  Container,
  Header,
  HeaderLogo,
  Content,
  SubHeader,
  AnalysisTitle,
  FormSection,
  FooterButtons,
  BackButton,
  SubmitButton,
  LoadingContainer,
  LoadingText,
  ProgressTrack,
  ProgressBar,
  ResultCard,
  CardTopRow,
  InsurerLogo,
  InsurerInfo,
  InsurerName,
  DetailsRow,
  DetailColumn,
  DetailLabel,
  DetailValue,
  ActionButtonsRow,
  FinalBackButton,
  ValidationContainer,
  ValidateTitle,
  ValidationSectionTitle,
  InputGroup,
  Row,
  StyledInput,
  ToggleInputContainer,
  StatusBadge,
  StatusDot,
  StatusText,
  Divider,
  RadioGroup,
  RadioButton,
  CustomRadio
} from '../styles';
import { FaChevronLeft, FaPencilAlt, FaCheck } from 'react-icons/fa';
import logorv from '../../../resources/imgs/logo-lado-bom-seguros.svg';
import Input from '../../../components/Input';
import { FieldLabel } from '../../../styles';
import { Dropdown } from 'semantic-ui-react';
import { warningNotification, successNotification } from '../../../services/notification';
import { unmaskCpfOrCnpj, numberToMoneyString } from '../../../services/masks';
import { get, post } from '../../../services/request';
import moment from 'moment-timezone';

import { useOccupations } from '../../fire-insurance/budget/forms-based-in-insurer/Segimob/services/useOccupations';
import { useAssistances } from '../../fire-insurance/budget/forms-based-in-insurer/Segimob/services/useAssistances';

interface SegimobBudgetFormProps {
  estate: any;
  insurerId: string;
  userId: string;
  percentage: number;
  onBudgetCreated?: (budgetId: string) => void;
  onBack: () => void;
}

type AnalysisStep = 'LOADING' | 'FORM' | 'RESULT';

const PwaSegimobBudgetForm: React.FC<SegimobBudgetFormProps> = ({
  estate,
  insurerId,
  userId,
  percentage,
  onBudgetCreated,
  onBack
}) => {
  const [step, setStep] = useState<AnalysisStep>('LOADING');
  const [loadingMsg, setLoadingMsg] = useState('Preparando sua cotação Tokio Marine...');
  const [resultData, setResultData] = useState<any>(null);
  const [installmentsOptions, setInstallmentsOptions] = useState<any[]>([]);
  const [coverages, setCoverages] = useState<any[]>([]);
  const [initialValues, setInitialValues] = useState<any>(null);

  const loadConfigs = useCallback(async () => {
    try {
      const configs = await get(`/estates/fire-active-insurer/${estate.id}/${insurerId}`);
      let options: any[] = [];
      if (configs.installmentOptions) {
        options = configs.installmentOptions.split(',').map((option: any) => ({
          key: option,
          text: `${option}x`,
          value: Number(option)
        }));
      }
      if (options.length === 0) {
        options = Array.from(Array(12).keys()).map(m => ({
          key: m + 1,
          text: `${m + 1}x`,
          value: m + 1
        }));
      }
      setInstallmentsOptions(options);
      return { configs, installments: options };
    } catch (e) {
      console.error(e);
      const defaultOptions = Array.from(Array(12).keys()).map(m => ({
        key: m + 1,
        text: `${m + 1}x`,
        value: m + 1
      }));
      setInstallmentsOptions(defaultOptions);
      return { configs: null, installments: defaultOptions };
    }
  }, [estate.id, insurerId]);

  const loadCoverages = useCallback(async () => {
    try {
      const cvgs = await get(
        `/segimob/coverage/coverage-estate/${estate.id}?insurerId=${insurerId}`
      );
      setCoverages(cvgs || []);
      return cvgs || [];
    } catch (e) {
      console.error(e);
      return [];
    }
  }, [estate.id, insurerId]);

  const initializeData = useCallback(async () => {
    setLoadingMsg('Carregando informações...');
    const [configsData, loadedCoverages] = await Promise.all([
      loadConfigs(),
      loadCoverages()
    ]);

    const initialCvgs = (loadedCoverages || []).map((c: any) => ({
      id: c.id,
      code: c.coverage?.code,
      checked: c.isRequired || c.coverage?.isDefault,
      value: 0,
      name: c.coverage?.name,
      onEdit: false,
      onEditValue: 0,
      maxValue: c.maxValue,
      minValue: c.minValue,
      multiplier: c.multiplier,
      isDefault: c.coverage?.isDefault,
      isRequired: c.isRequired,
      maxMultiplier: c.coverage?.maxMultiplier || c.maxMultiplier
    }));

    setInitialValues({
      propertyType: '1',
      propertyUseType: null,
      assistanceId: null,
      rentValue: '',
      installments: 11,
      startVigency: moment().format('YYYY-MM-DD'),
      endVigency: moment().add(12, 'months').format('YYYY-MM-DD'),
      months: 12,
      coverages: initialCvgs
    });

    setStep('FORM');
  }, [estate.id, insurerId, loadConfigs, loadCoverages]);

  useEffect(() => {
    initializeData();
  }, [initializeData]);

  const propertyTypesOptions = [
    { key: '1', text: 'Residencial', value: '1' },
    { key: '2', text: 'Comercial', value: '2' }
  ];

  const monthsOptions = Array.from(Array(12).keys()).map(m => ({
    key: m + 1,
    text: `${m + 1} Meses`,
    value: m + 1
  }));

  const validationSchema = Yup.object().shape({
    propertyType: Yup.string().required('Obrigatório'),
    propertyUseType: Yup.number().required('Obrigatório'),
    assistanceId: Yup.number().required('Obrigatório'),
    startVigency: Yup.string().required('Obrigatório'),
    rentValue: Yup.string().required('Obrigatório'),
    months: Yup.number().required('Obrigatório')
  });

  const handleSubmit = async (values: any, { setSubmitting }: any) => {
    setLoadingMsg('Enviando cotação para a Seguradora...');
    setStep('LOADING');
    try {
      const quotePayload = {
        estateId: estate.id,
        startDate: values.startVigency,
        assistanceId: values.assistanceId,
        changedRentValue: true,
        vigency: values.months,
        insuranceTypeId: values.propertyType,
        occupationId: values.propertyUseType,
        data: {
          rentValue: values.rentValue
        },
        proLaborePercentageValue: percentage || 0,
        percentualPredio: 90,
        percentualConteudo: 10,
        targetValue: 0,
        coverages: values.coverages
          .filter((c: any) => c.checked)
          .map((c: any) => {
            let val = c.value;
            if (!val && c.multiplier && values.rentValue) {
              val = values.rentValue * c.multiplier;
            }
            return {
              id: c.id,
              valorImportanciaSegurada: Number(val),
              flagSelecionada: true,
              valorPremioTotal: 0
            };
          })
      };

      const quoteRes: any = await post(
        '/fire-insurance-quotations/segimob/quote',
        quotePayload
      );

      if (!quoteRes || !quoteRes.totalValue) {
        throw new Error('Cotação falhou');
      }

      const budgetPayload = {
        estateId: estate.id,
        insurerId,
        isIframe: false,
        propertyType: values.propertyType,
        payload: {
          codigoExternoImovel: '',
          quantidadeParcelasSelecionada: values.installments,
          valorParcela:
            quoteRes.installments?.[0]?.valorParcela ||
            quoteRes.totalValue / values.installments,
          calculo: {
            idSeguradora: 'TOKIO_MARINE',
            idCorretora: '221',
            idImobiliaria: estate.segimobCode,
            idOcupacao: values.propertyUseType,
            idAssistencia: values.assistanceId,
            valorAluguel: values.rentValue,
            dataInicialVigencia: values.startVigency,
            mesesVigencia: values.months,
            valorPremioTotal: quoteRes.totalValue,
            valorPercentualProlabore: percentage || 0,
            valorPercentualComissaoCorretora:
              quoteRes.brokerCommissionPercentageValue || 0,
            flagAluguelAlterado: true,
            valorPercentualPredio: 90,
            valorPercentualConteudo: 10,
            coberturas: quoteRes.coverages || []
          },
          endereco: {
            cep: estate.estateZipCode,
            logradouro: estate.estateStreet,
            numero: estate.estateNumber,
            complemento: estate.estateComplement,
            bairro: estate.estateDistrict,
            uf: estate.estateState,
            cidade: estate.estateCity
          },
          inquilino: {
            nome: estate.name || 'Inquilino',
            cpfCnpj: estate.cnpj ? unmaskCpfOrCnpj(estate.cnpj) : '',
            email: estate.email || '',
            dataNascimento: '1990-01-01',
            numeroTelefone: estate.phone ? estate.phone.replace(/\D/g, '') : ''
          },
          proprietario: {
            nome: 'Proprietário',
            cpfCnpj: '',
            email: ''
          }
        }
      };

      const budgetRes: any = await post(
        '/fire-insurance-quotations/segimob/budget',
        budgetPayload
      );

      if (budgetRes && budgetRes.fireInsuranceId) {
        setResultData({
          maxPremium: quoteRes.totalValue,
          fireInsuranceId: budgetRes.fireInsuranceId
        });
        if (onBudgetCreated) onBudgetCreated(budgetRes.fireInsuranceId);
        successNotification('Sucesso', 'Orçamento criado com sucesso!');
        setStep('RESULT');
      } else {
        warningNotification('Erro', 'Falha ao salvar orçamento.');
        setStep('FORM');
      }
    } catch (e) {
      console.error(e);
      warningNotification('Ops', 'Ocorreu um erro ao realizar a cotação.');
      setStep('FORM');
    } finally {
      setSubmitting(false);
    }
  };

  const SegimobFormBody = ({ values, setFieldValue, isSubmitting, submitForm }: any) => {
    const { occupations, loading: loadingOccupations } = useOccupations(
      estate.id,
      values.propertyType
    );
    const {
      assistances,
      loading: loadingAssistances,
      defaultAssistanceId
    } = useAssistances(estate.id, values.propertyUseType);

    useEffect(() => {
      if (defaultAssistanceId && !values.assistanceId) {
        setFieldValue('assistanceId', defaultAssistanceId);
      } else if (assistances.length === 1 && !values.assistanceId) {
        setFieldValue('assistanceId', assistances[0].value);
      }
    }, [defaultAssistanceId, values.assistanceId, assistances, setFieldValue]);

    useEffect(() => {
      if (occupations.length === 1 && !values.propertyUseType) {
        setFieldValue('propertyUseType', occupations[0].value);
      }
    }, [occupations, values.propertyUseType, setFieldValue]);

    useEffect(() => {
      if (values.months) {
        const endDate = moment().add(values.months, 'months').format('YYYY-MM-DD');
        setFieldValue('endVigency', endDate);
      }
    }, [values.months, setFieldValue]);

    const handleRentValueBlur = () => {
      values.coverages.forEach((c: any, index: number) => {
        if (!c.checked) return;
        const multiplier = c.maxMultiplier
          ? c.maxMultiplier < c.multiplier
            ? c.maxMultiplier
            : c.multiplier
          : c.multiplier;
        const val = (values.rentValue || 0) * multiplier;
        if (val > c.maxValue) {
          setFieldValue(`coverages[${index}].value`, c.maxValue);
          setFieldValue(`coverages[${index}].onEditValue`, c.maxValue);
          return;
        }
        if (val < c.minValue) {
          setFieldValue(`coverages[${index}].value`, c.minValue);
          setFieldValue(`coverages[${index}].onEditValue`, c.minValue);
          return;
        }
        setFieldValue(`coverages[${index}].value`, val);
        setFieldValue(`coverages[${index}].onEditValue`, val);
      });
    };

    return (
      <FormSection>
        <ValidationSectionTitle>Dados do Seguro</ValidationSectionTitle>
        <InputGroup>
          <FieldLabel>Tipo do Imóvel</FieldLabel>
          <RadioGroup>
            {propertyTypesOptions.map(opt => (
              <RadioButton
                key={opt.key}
                active={values.propertyType === opt.value}
                onClick={() => {
                  setFieldValue('propertyType', opt.value);
                  setFieldValue('propertyUseType', null);
                  setFieldValue('assistanceId', null);
                }}
              >
                <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                  <CustomRadio active={values.propertyType === opt.value} />
                  <span>{opt.text}</span>
                </div>
              </RadioButton>
            ))}
          </RadioGroup>
        </InputGroup>

        <InputGroup>
          <FieldLabel>Atividade Principal / Ocupação</FieldLabel>
          <Dropdown
            fluid
            selection
            search
            options={occupations}
            loading={loadingOccupations}
            value={values.propertyUseType}
            onChange={(_, { value }) => {
              setFieldValue('propertyUseType', value);
              setFieldValue('assistanceId', null);
            }}
            placeholder="Selecione a ocupação..."
          />
        </InputGroup>

        {values.propertyUseType && (
          <InputGroup>
            <FieldLabel>Assistência</FieldLabel>
            <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
              {assistances.map((assistance: any) => (
                <RadioButton
                  key={assistance.value}
                  active={values.assistanceId === assistance.value}
                  onClick={() => setFieldValue('assistanceId', assistance.value)}
                  style={{ width: '100%', justifyContent: 'flex-start' }}
                >
                  <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                    <div
                      style={{
                        width: '20px',
                        height: '20px',
                        borderRadius: '50%',
                        border: `2px solid ${
                          values.assistanceId === assistance.value ? '#59249B' : '#DFDFDF'
                        }`,
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center'
                      }}
                    >
                      {values.assistanceId === assistance.value && (
                        <div
                          style={{
                            width: '10px',
                            height: '10px',
                            borderRadius: '50%',
                            background: '#59249B'
                          }}
                        />
                      )}
                    </div>
                    <span style={{ fontSize: '14px', textAlign: 'left' }}>
                      {assistance.text}
                    </span>
                  </div>
                </RadioButton>
              ))}
            </div>
          </InputGroup>
        )}

        <div style={{ display: 'flex', flexDirection: 'column', gap: '12px', width: '100%' }}>
          <InputGroup style={{ width: '100%' }}>
            <Input
              name="rentValue"
              label="Valor do Aluguel"
              placeholder="R$ 0,00"
              value={values.rentValue ? numberToMoneyString(values.rentValue) : ''}
              onChange={(e: any) => {
                const onlyNumber = e.target.value.replace(/\D/g, '');
                const value = Number(onlyNumber) / 100;
                setFieldValue('rentValue', value);
              }}
              onBlur={handleRentValueBlur}
            />
          </InputGroup>
          <InputGroup style={{ width: '100%' }}>
            <FieldLabel>Parcelas</FieldLabel>
            <Dropdown
              fluid
              selection
              options={
                installmentsOptions.length > 0 ? installmentsOptions : monthsOptions
              }
              value={values.installments}
              onChange={(_, { value }) => setFieldValue('installments', value)}
            />
          </InputGroup>
        </div>

        <InputGroup>
          <FieldLabel>Meses de Vigência</FieldLabel>
          <Dropdown
            fluid
            selection
            options={monthsOptions}
            value={values.months}
            onChange={(_, { value }) => setFieldValue('months', value)}
            placeholder="Selecione os meses de vigência"
          />
        </InputGroup>

        <div
          style={{
            display: 'flex',
            flexDirection: 'column',
            gap: '10px',
            padding: '16px',
            background: '#F3F4F6',
            borderRadius: '8px',
            border: '1px solid #E5E7EB'
          }}
        >
          <span
            style={{
              fontSize: 12,
              fontWeight: 700,
              color: '#6B7280',
              textTransform: 'uppercase'
            }}
          >
            Período de Vigência
          </span>
          <div style={{ display: 'flex', gap: '16px', alignItems: 'center' }}>
            <div style={{ flex: 1 }}>
              <FieldLabel>Início</FieldLabel>
              <ToggleInputContainer style={{ background: '#FFFFFF' }}>
                <StyledInput
                  type="date"
                  value={values.startVigency}
                  onChange={e => setFieldValue('startVigency', e.target.value)}
                />
              </ToggleInputContainer>
            </div>
            <div style={{ flex: 1 }}>
              <FieldLabel>Término previsto</FieldLabel>
              <div
                style={{
                  height: '42px',
                  display: 'flex',
                  alignItems: 'center',
                  padding: '0 5px',
                  color: '#4B5563',
                  fontWeight: 600,
                  fontSize: '15px'
                }}
              >
                {moment(values.endVigency).format('DD/MM/YYYY')}
              </div>
            </div>
          </div>
        </div>

        {values.coverages && values.coverages.length > 0 && (
          <>
            <ValidationSectionTitle style={{ marginTop: 16, marginBottom: 8 }}>
              Coberturas Disponíveis
            </ValidationSectionTitle>
            <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
              {values.coverages.map((cov: any, index: number) => (
                <div
                  key={cov.code}
                  style={{
                    display: 'flex',
                    flexDirection: 'column',
                    padding: '12px 16px',
                    background: '#F9FAFB',
                    borderRadius: '8px',
                    border: '1px solid #E5E7EB'
                  }}
                >
                  <div
                    style={{
                      display: 'flex',
                      justifyContent: 'space-between',
                      alignItems: 'center'
                    }}
                  >
                    <div
                      style={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: '10px',
                        flex: 1,
                        paddingRight: '8px'
                      }}
                    >
                      <input
                        type="checkbox"
                        checked={cov.checked}
                        disabled={cov.isRequired}
                        style={{
                          width: '18px',
                          height: '18px',
                          accentColor: '#59249B',
                          cursor: cov.isRequired ? 'not-allowed' : 'pointer'
                        }}
                        onChange={() => {
                          if (cov.isRequired) return;
                          setFieldValue(`coverages[${index}].checked`, !cov.checked);
                        }}
                      />
                      <span
                        style={{
                          fontSize: 14,
                          color: cov.checked ? '#111827' : '#9CA3AF',
                          fontWeight: cov.checked ? 600 : 400,
                          lineHeight: 1.2
                        }}
                      >
                        {cov.name}{' '}
                        {cov.isRequired && (
                          <span
                            style={{
                              color: '#EF4444',
                              fontSize: 10,
                              display: 'block',
                              marginTop: '2px',
                              fontWeight: 700
                            }}
                          >
                            OBRIGATÓRIO
                          </span>
                        )}
                      </span>
                    </div>

                    {cov.checked && (
                      <div
                        style={{
                          display: 'flex',
                          alignItems: 'center',
                          gap: '8px',
                          minWidth: '100px',
                          justifyContent: 'flex-end'
                        }}
                      >
                        {!cov.onEdit ? (
                          <>
                            <strong
                              style={{
                                fontSize: 15,
                                color: '#111827',
                                whiteSpace: 'nowrap'
                              }}
                            >
                              {numberToMoneyString(cov.value || 0)}
                            </strong>
                            <FaPencilAlt
                              size={14}
                              color="#6B7280"
                              style={{
                                cursor: 'pointer',
                                padding: '4px',
                                boxSizing: 'content-box'
                              }}
                              onClick={() => {
                                setFieldValue(`coverages[${index}].onEdit`, true);
                                setFieldValue(
                                  `coverages[${index}].onEditValue`,
                                  cov.value || cov.minValue
                                );
                              }}
                            />
                          </>
                        ) : (
                          <div
                            style={{ display: 'flex', alignItems: 'center', gap: '6px' }}
                          >
                            <StyledInput
                              placeholder="R$ 0,00"
                              style={{
                                width: '90px',
                                padding: '6px',
                                fontSize: '13px',
                                textAlign: 'right',
                                border: '1px solid #D1D5DB',
                                borderRadius: '4px',
                                margin: 0
                              }}
                              value={
                                cov.onEditValue
                                  ? numberToMoneyString(cov.onEditValue)
                                  : ''
                              }
                              onChange={(e: any) => {
                                const onlyNumber = e.target.value.replace(/\D/g, '');
                                const val = Number(onlyNumber) / 100;
                                setFieldValue(`coverages[${index}].onEditValue`, val);
                              }}
                            />
                            <FaCheck
                              size={18}
                              color="#10B981"
                              style={{
                                cursor: 'pointer',
                                padding: '4px',
                                boxSizing: 'content-box',
                                background: '#D1FAE5',
                                borderRadius: '50%'
                              }}
                              onClick={() => {
                                const val = cov.onEditValue;
                                if (val < cov.minValue || val > cov.maxValue) {
                                  warningNotification(
                                    'Valor inválido',
                                    `O valor para ${
                                      cov.name
                                    } deve ser entre ${numberToMoneyString(
                                      cov.minValue
                                    )} e ${numberToMoneyString(cov.maxValue)}`
                                  );
                                  return;
                                }
                                if (
                                  cov.maxMultiplier &&
                                  values.rentValue &&
                                  val > cov.maxMultiplier * values.rentValue
                                ) {
                                  warningNotification(
                                    'Valor limite!',
                                    `O valor inserido excede o limite configurado pelo multiplicador (R$ ${numberToMoneyString(
                                      cov.maxMultiplier * values.rentValue
                                    )}).`
                                  );
                                  return;
                                }
                                setFieldValue(`coverages[${index}].value`, val);
                                setFieldValue(`coverages[${index}].onEdit`, false);
                              }}
                            />
                          </div>
                        )}
                      </div>
                    )}
                  </div>
                </div>
              ))}
            </div>
          </>
        )}

        <FooterButtons>
          <BackButton onClick={onBack} disabled={isSubmitting}>
            Cancelar
          </BackButton>
          <SubmitButton
            type="button"
            onClick={() => submitForm()}
            disabled={isSubmitting || !values.assistanceId}
          >
            {isSubmitting ? 'Cotando...' : 'Cotar Agora'}
          </SubmitButton>
        </FooterButtons>
      </FormSection>
    );
  };

  if (step === 'LOADING') {
    return (
      <Container>
        <Header>
          <HeaderLogo src={logorv} alt="Logo" />
        </Header>
        <LoadingContainer>
          <LoadingText>{loadingMsg}</LoadingText>
          <ProgressTrack>
            <ProgressBar />
          </ProgressTrack>
        </LoadingContainer>
      </Container>
    );
  }

  if (step === 'RESULT' && resultData) {
    return (
      <Container>
        <Header>
          <button
            style={{
              background: 'none',
              border: 'none',
              color: '#59249b',
              display: 'flex',
              alignItems: 'center',
              gap: 4
            }}
            onClick={onBack}
          >
            <FaChevronLeft /> Voltar
          </button>
          <HeaderLogo src={logorv} alt="Logo" />
        </Header>
        <Content>
          <SubHeader>
            <AnalysisTitle>Resultado da Cotação</AnalysisTitle>
          </SubHeader>
          <ResultCard>
            <CardTopRow>
              <InsurerLogo color="#009B74">TM</InsurerLogo>
              <InsurerInfo>
                <InsurerName>Tokio Marine (Segimob)</InsurerName>
                <StatusBadge type="approved">
                  <StatusDot type="approved" />
                  <StatusText>Pronto para Contratação</StatusText>
                </StatusBadge>
              </InsurerInfo>
            </CardTopRow>
            <Divider />
            <DetailsRow>
              <DetailColumn>
                <DetailLabel>Prêmio Total</DetailLabel>
                <DetailValue>{numberToMoneyString(resultData.maxPremium)}</DetailValue>
              </DetailColumn>
              <DetailColumn style={{ alignItems: 'flex-end' }}>
                <DetailLabel>ID do Orçamento</DetailLabel>
                <DetailValue>
                  {resultData.fireInsuranceId?.split('-')[0] || 'N/A'}
                </DetailValue>
              </DetailColumn>
            </DetailsRow>
          </ResultCard>

          <ActionButtonsRow style={{ marginTop: 'auto' }}>
            <FinalBackButton onClick={onBack}>Novo Orçamento / Voltar</FinalBackButton>
          </ActionButtonsRow>
        </Content>
      </Container>
    );
  }

  return (
    <Container>
      <Header>
        <button
          style={{
            background: 'none',
            border: 'none',
            color: '#59249b',
            display: 'flex',
            alignItems: 'center',
            gap: 4
          }}
          onClick={onBack}
        >
          <FaChevronLeft /> Voltar
        </button>
        <HeaderLogo src={logorv} alt="Logo" />
      </Header>

      <Content>
        <ValidationContainer>
          <ValidateTitle>
            <span>Tokio Marine (Segimob)</span>
          </ValidateTitle>

          {initialValues && (
            <Formik
              enableReinitialize
              initialValues={initialValues}
              validationSchema={validationSchema}
              onSubmit={handleSubmit}
            >
              {({
                values,
                errors,
                touched,
                handleChange,
                setFieldValue,
                submitForm,
                isSubmitting
              }) => (
                <SegimobFormBody
                  values={values}
                  setFieldValue={setFieldValue}
                  isSubmitting={isSubmitting}
                  submitForm={submitForm}
                />
              )}
            </Formik>
          )}
        </ValidationContainer>
      </Content>
    </Container>
  );
};

export default PwaSegimobBudgetForm;
